import { z } from "zod";

// Response contracts — mirror crates/humanai-core/src/types.rs (token model).
// We validate at the boundary so the UI never renders unvalidated server data.

export const RoundDraft = z.object({
  round: z.number(),
  text: z.string(),
  cos: z.number().nullable().optional(),
  recommended: z.boolean().optional(),
  fidelity_ok: z.boolean().nullable().optional(),
});
export type RoundDraft = z.infer<typeof RoundDraft>;

export const HumanizeResponse = z.object({
  output: z.string(),
  round_used: z.number().nullable().optional(),
  cos: z.number().nullable().optional(),
  fidelity_ok: z.boolean().nullable().optional(),
  request_id: z.string(),
  drafts: z.array(RoundDraft).optional().default([]),
});
export type HumanizeResponse = z.infer<typeof HumanizeResponse>;

// POST /humanize/stream — optimistic-streaming SSE events. `delta` text is a
// raw in-flight draft (rendered live, may be superseded); `round_done` carries
// the cleaned round; `done` is the committed HumanizeResponse.
export const HumanizeStreamEvent = z.discriminatedUnion("type", [
  // Emitted once per second while waiting for a GPU slot (FIFO queue).
  z.object({ type: z.literal("queued"), position: z.number(), eta_secs: z.number() }),
  // Long inputs are rewritten in parallel chunks; this plan event tells the
  // client how many live panes to render (deltas/resets carry `segment`).
  z.object({ type: z.literal("segments"), total: z.number() }),
  z.object({ type: z.literal("round"), round: z.number(), total: z.number() }),
  z.object({
    type: z.literal("delta"),
    round: z.number(),
    text: z.string(),
    segment: z.number().optional(),
  }),
  z.object({ type: z.literal("reset"), round: z.number(), segment: z.number().optional() }),
  z.object({
    type: z.literal("round_done"),
    round: z.number(),
    total: z.number().optional(),
    cos: z.number().nullable().optional(),
    ok: z.boolean().optional(),
    text: z.string(),
  }),
  HumanizeResponse.extend({ type: z.literal("done") }),
  z.object({ type: z.literal("error"), message: z.string() }),
]);
export type HumanizeStreamEvent = z.infer<typeof HumanizeStreamEvent>;

// GET /humanize/progress/{id} — live per-round progress (read-only side-channel).
export const ProgressResponse = z.object({
  phase: z.string(), // "running" | "done" | "unknown"
  round: z.number().nullable().optional(),
  total: z.number().nullable().optional(),
  cos: z.number().nullable().optional(),
  drafts: z.array(RoundDraft).optional().default([]),
});
export type ProgressResponse = z.infer<typeof ProgressResponse>;

export const UsageRecord = z.object({
  ts: z.string(),
  input_chars: z.number().nullable().optional(),
  rounds: z.number().nullable().optional(),
  tokens_charged: z.number(),
  request_id: z.string(),
  // "humanize" | "detect"; older servers omit it.
  kind: z.string().optional().default("humanize"),
});
export type UsageRecord = z.infer<typeof UsageRecord>;

export const MeResponse = z.object({
  free_tokens: z.number(),
  paid_tokens: z.number(),
  recent_usage: z.array(UsageRecord).default([]),
});
export type MeResponse = z.infer<typeof MeResponse>;

// `pay_url` is present only on fiat (Alipay) orders; crypto orders instead
// return pay_to/chain/token/amount/expires_at (all omitted when not applicable).
export const CreateOrderResponse = z.object({
  out_trade_no: z.string(),
  pay_url: z.string().optional(),
  pay_to: z.string().optional(),
  chain: z.string().optional(),
  token: z.string().optional(),
  amount: z.string().optional(),
  expires_at: z.string().optional(),
});
export type CreateOrderResponse = z.infer<typeof CreateOrderResponse>;

// POST /billing/crypto/submit — 200 only; 400 is surfaced via ApiError (the
// server dedups by tx_hash, so no Idempotency-Key is required).
export const CryptoSubmitResponse = z.object({
  status: z.enum(["paid", "pending"]),
  confirmations: z.number().optional(),
  needed: z.number().optional(),
});
export type CryptoSubmitResponse = z.infer<typeof CryptoSubmitResponse>;

// ---- POST /detect --------------------------------------------------------

export const DetectSentence = z.object({
  text: z.string(),
  ai_prob: z.number(),
  flagged: z.boolean(),
});
export type DetectSentence = z.infer<typeof DetectSentence>;

// `cached` ⇒ served from a stored score, nothing charged. `checked_at` is
// when the detector scored it — always shown, since detectors retrain.
export const DetectResponse = z.object({
  provider: z.string(),
  verdict: z.string(),
  human_prob: z.number(),
  ai_prob: z.number(),
  mixed_prob: z.number().nullable().optional(),
  sentences: z.array(DetectSentence).optional().default([]),
  tokens_charged: z.number(),
  cached: z.boolean(),
  checked_at: z.string(),
  request_id: z.string(),
});
export type DetectResponse = z.infer<typeof DetectResponse>;

// ---- Auth & account keys (DESIGN-service.md §3.2) -----------------------

export const SignupResponse = z.object({
  account_id: z.string(),
  jwt: z.string(),
  // The account's first API key, plaintext, returned exactly once.
  api_key: z.string(),
});
export type SignupResponse = z.infer<typeof SignupResponse>;

export const LoginResponse = z.object({ jwt: z.string() });
export type LoginResponse = z.infer<typeof LoginResponse>;

/** Public signup feature flags (GET /auth/config). */
export const AuthConfigResponse = z.object({
  email_verification: z.boolean(),
  // Present ⇒ "Sign in with Google" is enabled server-side.
  google_client_id: z.string().optional(),
  // Present ⇒ POST /detect is available; the configured detector's id.
  detect_provider: z.string().optional(),
});
export type AuthConfigResponse = z.infer<typeof AuthConfigResponse>;

/** POST /auth/google — api_key is present only when a new account was created. */
export const GoogleAuthResponse = z.object({
  account_id: z.string(),
  email: z.string(),
  jwt: z.string(),
  api_key: z.string().optional(),
});
export type GoogleAuthResponse = z.infer<typeof GoogleAuthResponse>;

export const SendCodeResponse = z.object({ sent: z.boolean() });
export type SendCodeResponse = z.infer<typeof SendCodeResponse>;

export const CreateKeyResponse = z.object({
  id: z.string(),
  api_key: z.string(),
  label: z.string().nullable().optional(),
});
export type CreateKeyResponse = z.infer<typeof CreateKeyResponse>;

export const KeyInfo = z.object({
  id: z.string(),
  label: z.string().nullable().optional(),
  created_at: z.string(),
  revoked_at: z.string().nullable().optional(),
});
export type KeyInfo = z.infer<typeof KeyInfo>;

export const KeyList = z.array(KeyInfo);
export type KeyList = z.infer<typeof KeyList>;

/** For body-less 204 responses (DELETE). */
export const NoContent = z.unknown();

export const OrderStatus = z.enum(["created", "paid", "failed", "closed"]);
export type OrderStatus = z.infer<typeof OrderStatus>;

export const OrderStatusResponse = z.object({
  out_trade_no: z.string(),
  package: z.string(),
  amount_cents: z.number(),
  tokens: z.number(),
  currency: z.string(),
  // Tolerate unknown server states without throwing.
  status: z.string(),
  alipay_trade_no: z.string().nullable().optional(),
  created_at: z.string(),
  paid_at: z.string().nullable().optional(),
  provider: z.string().optional(),
  pay_url: z.string().nullable().optional(),
  payment: CreateOrderResponse.nullable().optional(),
});
export type OrderStatusResponse = z.infer<typeof OrderStatusResponse>;

export const OrderList = z.array(OrderStatusResponse);
